import React, { useState } from 'react';

import { useMutation } from '@apollo/client';
import { faFileSignature } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress, Grid, TextField } from '@mui/material';
import { Formik } from 'formik';
import { toast, ToastContainer } from 'react-toastify';

import auth from '../../../../../services/Auth';
import candidateSchema from '../../../../../services/candidate/gqlSchema';
import { sepaSchema } from '../../validators/sepa';

const SepaForm = () => {
  const [candidate] = auth.findContactsInfos({
    sources: {
      source: 'SALESFORCE',
      referer: 'INTRANET',
      section: 'CANDIDATE',
    },
  });
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const [sepaDetailCreate] = useMutation(candidateSchema.Mutation.sepaDetailCreateMutation);
  const [requestSepaSignature] = useMutation(candidateSchema.Mutation.requestSepaSignatureMutation);

  const initialValues = {
    iban: '',
    bic: '',
    address: candidate.address || '',
    zipcode: candidate.zipcode || '',
    city: candidate.city || '',
  };

  const onSubmit = async values => {
    setSubmitInProgress(true);

    try {
      const params = {
        bic: values.bic,
        iban: values.iban,
        address: values.address,
        city: values.city,
        country: candidate.country || 'France',
        name: [candidate.firstname, candidate.name].join(' '),
        postalCode: values.zipcode,
        contactId: auth.getContact().id,
      };

      // Save to DB first
      const {
        data: { sepaDetailCreate: sepaDetailId },
      } = await sepaDetailCreate({ variables: { params: { ...params, filename: 'SEPA' } } });

      // Request Universign afterwards
      const { data: signData } = await requestSepaSignature({
        variables: {
          params: {
            ...params,
            fileName: 'SEPA',
            companyCode: candidate.company.code,
            sepaDetailId,
          },
        },
      });

      window.location.assign(signData.requestSepaSignature.url);
    } catch (error) {
      toast.error(error.message);
    }

    setSubmitInProgress(false);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={sepaSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={'IBAN (International Bank Account Number)'}
                id={'iban'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                onChange={e => {
                  setFieldValue('iban', e.target.value.toUpperCase());
                }}
                onBlur={handleBlur}
                value={values.iban}
                error={!!errors.iban}
                helperText={errors.iban || 'IBAN complet sans séparateurs'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={'BIC (Bank Identifier Code)'}
                id={'bic'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                onChange={e => {
                  setFieldValue('bic', e.target.value.toUpperCase());
                }}
                onBlur={handleBlur}
                value={values.bic}
                error={!!errors.bic}
                helperText={errors.bic || 'BIC complet sans séparateurs'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={'Adresse'}
                id={'address'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.address}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={'Code postal'}
                id={'zipcode'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.zipcode}
                error={!!errors.zipcode}
                helperText={errors.zipcode}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={'Ville'}
                id={'city'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.city}
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  onClick={handleSubmit}
                  disabled={submitInProgress}
                  startIcon={
                    submitInProgress ? <CircularProgress size={'1rem'} /> : <FontAwesomeIcon icon={faFileSignature} />
                  }
                >
                  <Box component='span' sx={{ marginLeft: '5px' }}>
                    Sauvegarder et signer
                  </Box>
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Formik>

      <ToastContainer />
    </>
  );
};

export default SepaForm;
